import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner";

/**
 * Toaster provider — place once in the root layout.
 * Automatically reads document.dir for RTL positioning.
 */
export function Toaster() {
  return (
    <SonnerToaster
      position={document.dir === "rtl" ? "bottom-left" : "bottom-right"}
      dir={document.dir as "ltr" | "rtl" | undefined}
      duration={5000}
      visibleToasts={3}
      closeButton
      toastOptions={{
        classNames: {
          toast: "font-sans",
        },
      }}
    />
  );
}

/**
 * Toast helpers — thin wrappers around Sonner's toast() that provide
 * a consistent API for the rest of the app.
 *
 * Usage:
 *   import { toast } from "@/components/ui/toast";
 *   toast.success("Campaign created");
 *   toast.error("Failed to save", { action: { label: "Retry", onClick: retry } });
 */
export const toast = {
  success(message: string, opts?: ToastOptions) {
    return sonnerToast.success(message, mapOptions(opts));
  },
  error(message: string, opts?: ToastOptions) {
    return sonnerToast.error(message, mapOptions(opts));
  },
  warning(message: string, opts?: ToastOptions) {
    return sonnerToast.warning(message, mapOptions(opts));
  },
  info(message: string, opts?: ToastOptions) {
    return sonnerToast.info(message, mapOptions(opts));
  },
  /** Plain toast with no icon */
  message(message: string, opts?: ToastOptions) {
    return sonnerToast(message, mapOptions(opts));
  },
  dismiss(id?: string | number) {
    return sonnerToast.dismiss(id);
  },
};

interface ToastOptions {
  description?: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
  dismissible?: boolean;
}

function mapOptions(opts?: ToastOptions) {
  if (!opts) return undefined;
  return {
    description: opts.description,
    duration: opts.duration,
    dismissible: opts.dismissible,
    action: opts.action
      ? { label: opts.action.label, onClick: opts.action.onClick }
      : undefined,
  };
}
